import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import CommonButton from "../../CommonButton";
import CartButton from "../CartButton";
import { RelatedProduct } from "../../../types/Response/CustomerResponse";

interface ProductCarouselSectionProp {
  showLogo?: boolean;
  title?: React.ReactNode;
  highlightText?: string;
  highlightClassName?: string;
  handleProductPage?: (productId: number) => void;
  productData: RelatedProduct[];
  showCartButton?: boolean;
}

const YouMayLikeProductSection: React.FC<ProductCarouselSectionProp> = ({
  handleProductPage,
  productData = [],
  showCartButton = true,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState<{ [key: number]: number }>({});
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [cartQty, setCartQty] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    if (hoveredIndex === null) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        const updated = { ...prev };
        const product = productData[hoveredIndex];

        if (!product) return prev;

        const images = [product.imageUrl];
        const current = updated[hoveredIndex] ?? 0;

        updated[hoveredIndex] = (current + 1) % images.length;

        return updated;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [hoveredIndex, productData]);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -300 : 300,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const handleCardClick = (productId: number) => {
    if (handleProductPage && productId) {
      handleProductPage(productId);
    }
  };

  const handleAdd = (index: number) => {
    setCartQty((prev) => ({
      ...prev,
      [index]: 1,
    }));
  };

  const handleIncrement = (index: number) => {
    setCartQty((prev) => ({
      ...prev,
      [index]: Math.min((prev[index] || 0) + 1, 10),
    }));
  };

  const handleDecrement = (index: number) => {
    setCartQty((prev) => {
      const current = prev[index];

      if (current === 1) {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      }

      return {
        ...prev,
        [index]: current - 1,
      };
    });
  };

  return (
    <section className="relative overflow-hidden w-full">
      <div className="relative z-10">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between relative gap-3 pb-3">
          <h2 className="text-xl md:text-2xl lg:text-2xl font-bold text-iconColor">
            You may also like
          </h2>
        </div>

        {/* =========================================================
            DESKTOP VIEW
        ========================================================== */}
        <div className="hidden md:grid grid-cols-[40px_1fr_40px] items-center gap-2">

          {/* LEFT ARROW */}
          <div className="flex justify-center">
            <CommonButton
              icon={<ChevronLeft size={20} />}
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="bg-white"
            />
          </div>

          {/* PRODUCTS */}
          <div
            ref={scrollRef}
            onScroll={() => handleScroll(scrollRef)}
            className="flex gap-4 overflow-x-auto [&::-webkit-scrollbar]:hidden py-2"
          >
            {productData.map((product, index) => {
              const currentImage = product.imageUrl;

              const original = product.price || 0;
              const salePrice = product.salePrice || 0;

              const hasPriceDiscount =
                salePrice > 0 && salePrice < original;

              const discount =
                original > 0 && hasPriceDiscount
                  ? Math.round(
                      ((original - salePrice) / original) * 100
                    )
                  : 0;

              const qty = cartQty[index] || 0;

              return (
                <div
                  key={product.productId || index}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => handleCardClick(product.productId)}
                  className="
                    group
                    w-[200px]
                    flex-shrink-0
                    rounded-xl
                    flex
                    flex-col
                    justify-between
                    border
                    border-gray-200
                    bg-white
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:shadow-lg
                    cursor-pointer
                  "
                >

                  {/* IMAGE SECTION */}
                  <div
                    className="
                      relative
                      h-[220px]
                      rounded-t-xl
                      flex
                      items-center
                      justify-center
                      overflow-hidden
                      bg-white
                    "
                  >
                    <img
                      src={currentImage}
                      alt={product.name}
                      className="
                        h-full
                        w-full
                        object-contain
                        p-2
                      "
                    />

                    {/* IMPORTANT:
                        Use > 0 so React does NOT render 0
                    */}
                    {Number(product?.rating) > 0 && (
                      <div
                        className="
                          absolute
                          bottom-2
                          left-2
                          z-20
                          bg-blue-800
                          px-2
                          py-1
                          rounded-r-lg
                          flex
                          items-center
                          gap-1
                        "
                      >
                        <Star
                          className="
                            w-2.5
                            h-2.5
                            fill-yellow-400
                            text-yellow-400
                          "
                        />

                        <span className="text-xs font-semibold text-white">
                          {product.rating}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* PRODUCT DETAILS */}
                  <div className="px-3 flex flex-col flex-1 justify-between pb-3 pt-2">

                    <p
                      className="
                        text-sm
                        text-logoHeading
                        font-semibold
                        line-clamp-2
                        min-h-[40px]
                      "
                    >
                      {product.name}
                    </p>

                    <div className="flex items-end justify-between mt-2">

                      {/* PRICE */}
                      <div className="flex flex-col gap-0.5">

                        {hasPriceDiscount ? (
                          <>
                            <div className="flex items-center gap-1.5">

                              <span className="text-xs text-gray-400 line-through">
                                AED {original}
                              </span>

                              <span
                                className="
                                  text-[10px]
                                  text-white
                                  bg-logoHeading
                                  px-1
                                  rounded
                                  font-semibold
                                "
                              >
                                -{discount}%
                              </span>

                            </div>

                            <span className="text-base font-bold text-logoHeading">
                              AED {salePrice}
                            </span>
                          </>
                        ) : (
                          <span className="text-base font-bold text-logoHeading">
                            AED {original}
                          </span>
                        )}

                      </div>

                      {/* CART BUTTON */}
                      {showCartButton && product.inStock && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <CartButton
                            qty={qty}
                            onAdd={() => handleAdd(index)}
                            onIncrement={() => handleIncrement(index)}
                            onDecrement={() => handleDecrement(index)}
                            size="sm"
                          />
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT ARROW */}
          <div className="flex justify-center">
            <CommonButton
              icon={<ChevronRight size={20} />}
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="bg-white"
            />
          </div>
        </div>

        {/* =========================================================
            MOBILE VIEW
        ========================================================== */}
        <div className="md:hidden flex flex-col gap-3">

          <div
            ref={mobileScrollRef}
            onScroll={() => handleScroll(mobileScrollRef)}
            className="
              flex
              gap-4
              overflow-x-auto
              snap-x
              snap-mandatory
              [&::-webkit-scrollbar]:hidden
              py-2
            "
          >
            {productData.map((product, index) => {
              const original = product.price || 0;
              const salePrice = product.salePrice || 0;

              const hasPriceDiscount =
                salePrice > 0 && salePrice < original;

              const discount =
                original > 0 && hasPriceDiscount
                  ? Math.round(
                      ((original - salePrice) / original) * 100
                    )
                  : 0;

              const qty = cartQty[index] || 0;

              return (
                <div
                  key={product.productId || index}
                  onClick={() => handleCardClick(product.productId)}
                  className="
                    w-[85vw]
                    flex-shrink-0
                    snap-center
                    rounded-2xl
                    border
                    border-gray-200
                    bg-white
                    overflow-hidden
                    flex
                    flex-col
                    justify-between
                    cursor-pointer
                  "
                >

                  {/* IMAGE */}
                  <div
                    className="
                      relative
                      bg-white
                      flex
                      items-center
                      justify-center
                      overflow-hidden
                    "
                    style={{
                      aspectRatio: "1.15 / 1",
                    }}
                  >
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="
                        h-full
                        w-full
                        object-contain
                        p-3
                      "
                    />

                    {/* FIXED: no 0 when rating is 0 */}
                    {Number(product?.rating) > 0 && (
                      <div
                        className="
                          absolute
                          bottom-2
                          left-2
                          z-20
                          bg-blue-800
                          px-2
                          py-1
                          rounded-r-lg
                          flex
                          items-center
                          gap-1
                        "
                      >
                        <Star
                          className="
                            w-2.5
                            h-2.5
                            fill-yellow-400
                            text-yellow-400
                          "
                        />

                        <span className="text-xs font-semibold text-white">
                          {product.rating}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* DETAILS */}
                  <div className="p-3">

                    <p
                      className="
                        text-sm
                        text-logoHeading
                        font-semibold
                        line-clamp-2
                        mb-2
                      "
                    >
                      {product.name}
                    </p>

                    <div className="flex items-end justify-between">

                      <div className="flex flex-col gap-1">

                        {hasPriceDiscount ? (
                          <>
                            <div className="flex items-center gap-1.5">

                              <span className="text-xs text-gray-400 line-through">
                                AED {original}
                              </span>

                              <span
                                className="
                                  text-[10px]
                                  text-white
                                  bg-logoHeading
                                  px-1.5
                                  rounded
                                "
                              >
                                Save {discount}%
                              </span>

                            </div>

                            <span className="text-base font-bold text-logoHeading">
                              AED {salePrice}
                            </span>
                          </>
                        ) : (
                          <span className="text-base font-bold text-logoHeading">
                            AED {original}
                          </span>
                        )}

                      </div>

                      {/* CART */}
                      {showCartButton && product.inStock && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <CartButton
                            qty={qty}
                            onAdd={() => handleAdd(index)}
                            onIncrement={() => handleIncrement(index)}
                            onDecrement={() => handleDecrement(index)}
                            size="sm"
                          />
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default YouMayLikeProductSection;